import { createContext, useContext, useState, useCallback, useRef } from 'react'
import { CheckCircle, XCircle, AlertTriangle, Info, X } from 'lucide-react'
import { useTheme } from './ThemeContext'

const ToastContext = createContext()

export const useToast = () => {
  const context = useContext(ToastContext)
  if (!context) {
    throw new Error('useToast must be used within ToastProvider')
  }
  return context
}

const Toast = ({ toast, onClose, theme = 'dark' }) => {

  const icons = {
    success: CheckCircle,
    error: XCircle,
    warning: AlertTriangle,
    info: Info
  }

  const colors = {
    success: {
      bg: theme === 'dark' ? 'bg-emerald-500/20' : 'bg-emerald-50',
      border: theme === 'dark' ? 'border-emerald-500/50' : 'border-emerald-200',
      text: theme === 'dark' ? 'text-emerald-400' : 'text-emerald-700',
      icon: 'text-emerald-500'
    },
    error: {
      bg: theme === 'dark' ? 'bg-red-500/20' : 'bg-red-50',
      border: theme === 'dark' ? 'border-red-500/50' : 'border-red-200',
      text: theme === 'dark' ? 'text-red-400' : 'text-red-700',
      icon: 'text-red-500'
    },
    warning: {
      bg: theme === 'dark' ? 'bg-yellow-500/20' : 'bg-yellow-50',
      border: theme === 'dark' ? 'border-yellow-500/50' : 'border-yellow-200',
      text: theme === 'dark' ? 'text-yellow-400' : 'text-yellow-700',
      icon: 'text-yellow-500'
    },
    info: {
      bg: theme === 'dark' ? 'bg-blue-500/20' : 'bg-blue-50',
      border: theme === 'dark' ? 'border-blue-500/50' : 'border-blue-200',
      text: theme === 'dark' ? 'text-blue-400' : 'text-blue-700',
      icon: 'text-blue-500'
    }
  }

  const Icon = icons[toast.type] || Info
  const colorScheme = colors[toast.type] || colors.info

  return (
    <div
      className={`
        ${colorScheme.bg} ${colorScheme.border} ${colorScheme.text}
        border rounded-lg shadow-lg p-4 mb-3 min-w-[320px] max-w-[500px]
        flex items-start gap-3 animate-slide-in-right
        backdrop-blur-sm
      `}
      style={{
        animation: 'slideInRight 0.3s ease-out'
      }}
    >
      <Icon size={20} className={`${colorScheme.icon} flex-shrink-0 mt-0.5`} />
      <div className="flex-1">
        {toast.title && (
          <div className="font-semibold mb-1">{toast.title}</div>
        )}
        <div className="text-sm">{toast.message}</div>
      </div>
      <button
        onClick={onClose}
        className={`
          flex-shrink-0 ml-2 p-1 rounded hover:bg-black/10 transition-colors
          ${colorScheme.text} hover:opacity-80
        `}
      >
        <X size={16} />
      </button>
    </div>
  )
}

export const ToastProvider = ({ children }) => {
  const { theme } = useTheme()
  // Audio Context Ref to reuse the context
  const audioCtxRef = useRef(null);
  const [toasts, setToasts] = useState([])

  const playSound = useCallback(() => {
    try {
      if (!audioCtxRef.current) {
        audioCtxRef.current = new (window.AudioContext || window.webkitAudioContext)();
      }

      const ctx = audioCtxRef.current;

      // Browser Autoplay Policy: Resume if suspended (must be triggered by user interaction at least once)
      if (ctx.state === 'suspended') {
        ctx.resume().catch(err => console.error("Audio resume failed", err));
      }

      const osc = ctx.createOscillator();
      const gain = ctx.createGain();

      // Louder, clearer "Ding"
      osc.type = 'sine';
      osc.frequency.setValueAtTime(800, ctx.currentTime);
      osc.frequency.exponentialRampToValueAtTime(400, ctx.currentTime + 0.15);

      // Increased Volume
      gain.gain.setValueAtTime(0.3, ctx.currentTime);
      gain.gain.exponentialRampToValueAtTime(0.01, ctx.currentTime + 0.15);

      osc.connect(gain);
      gain.connect(ctx.destination);

      osc.start();
      osc.stop(ctx.currentTime + 0.15);

    } catch (e) {
      console.warn("Audio play failed", e);
    }
  }, []);

  const showToast = useCallback((message, type = 'info', duration = 5000, title = null) => {
    const id = Date.now() + Math.random()
    const toast = { id, message, type, title }

    setToasts(prev => [...prev, toast])
    playSound();

    if (duration > 0) {
      setTimeout(() => {
        setToasts(prev => prev.filter(t => t.id !== id))
      }, duration)
    }

    return id
  }, [playSound])

  const removeToast = useCallback((id) => {
    setToasts(prev => prev.filter(t => t.id !== id))
  }, [])

  const success = useCallback((message, title = null, duration = 5000) => {
    return showToast(message, 'success', duration, title)
  }, [showToast])

  const error = useCallback((message, title = null, duration = 6000) => {
    return showToast(message, 'error', duration, title)
  }, [showToast])

  const warning = useCallback((message, title = null, duration = 5000) => {
    return showToast(message, 'warning', duration, title)
  }, [showToast])

  const info = useCallback((message, title = null, duration = 5000) => {
    return showToast(message, 'info', duration, title)
  }, [showToast])

  return (
    <ToastContext.Provider value={{ success, error, warning, info, showToast, removeToast }}>
      {children}
      <div
        className="fixed top-20 right-4 z-50 pointer-events-none"
        style={{ maxHeight: 'calc(100vh - 100px)', overflowY: 'auto' }}
      >
        <style>{`
          @keyframes slideInRight {
            from {
              transform: translateX(100%);
              opacity: 0;
            }
            to {
              transform: translateX(0);
              opacity: 1;
            }
          }
          .animate-slide-in-right {
            animation: slideInRight 0.3s ease-out;
          }
        `}</style>
        <div className="pointer-events-auto">
          {toasts.map(toast => (
            <Toast
              key={toast.id}
              toast={toast}
              onClose={() => removeToast(toast.id)}
              theme={theme}
            />
          ))}
        </div>
      </div>
    </ToastContext.Provider>
  )
}

